import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
type Invoice = {
  id: string;
  customer: string;
  reference: string;
  amount: number;
  age: string;
};
const invoices: Invoice[] = [{
  id: 'inv-1',
  customer: 'Harding Joinery Ltd',
  reference: 'INV-2841',
  amount: 4820,
  age: '18 days overdue'
}, {
  id: 'inv-2',
  customer: 'Northgate Electrical',
  reference: 'INV-2839',
  amount: 1975,
  age: '11 days overdue'
}, {
  id: 'inv-3',
  customer: 'Selby Plant Hire',
  reference: 'INV-2836',
  amount: 7640,
  age: '9 days overdue'
}, {
  id: 'inv-4',
  customer: 'Ravenscroft Interiors',
  reference: 'INV-2830',
  amount: 3150,
  age: '5 days overdue'
}, {
  id: 'inv-5',
  customer: 'Wilcox Fabrication',
  reference: 'INV-2827',
  amount: 2480,
  age: '3 days overdue'
}];
const currency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0
});
const easeOut = [0.23, 1, 0.32, 1] as const;
const sparkles = Array.from({
  length: 10
}, (_, index) => {
  const angle = index / 10 * Math.PI * 2;
  return {
    x: Math.cos(angle) * 46,
    y: Math.sin(angle) * 34
  };
});
function PaidCelebration() {
  return <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-8 grid place-items-center" data-id="element-89" data-mp="3uz5n3oe33lwt">
      <span className="relative block h-0 w-0" data-id="element-90" data-mp="dhcosf0wz2br">
        <motion.span initial={{
        opacity: 0.85,
        scale: 0.3
      }} animate={{
        opacity: 0,
        scale: 2.4
      }} transition={{
        duration: 0.6,
        ease: easeOut
      }} className="absolute rounded-full border-[3px] border-[#4BE4FF]" style={{
        height: 84,
        width: 84,
        left: -42,
        top: -42
      }} data-id="element-91" data-mp="dk8n44gobt0q" />
        <motion.span initial={{
        opacity: 0.45,
        scale: 0.2
      }} animate={{
        opacity: 0,
        scale: 1.5
      }} transition={{
        duration: 0.5,
        ease: easeOut,
        delay: 0.06
      }} className="absolute rounded-full bg-[#4BE4FF]" style={{
        height: 64,
        width: 64,
        left: -32,
        top: -32
      }} data-id="element-92" data-mp="2oa76odkzsvsa" />
        {sparkles.map((sparkle, index) => <motion.span key={index} initial={{
        opacity: 1,
        scale: 1.2,
        x: 0,
        y: 0
      }} animate={{
        opacity: 0,
        scale: 0.4,
        x: sparkle.x,
        y: sparkle.y
      }} transition={{
        duration: 0.62,
        ease: easeOut,
        delay: index * 0.02
      }} className="absolute h-2 w-2 rounded-full bg-[#4BE4FF]" data-id="element-93" data-mp="s9eu328cg9r3" data-mpi={index} />)}
      </span>
    </span>;
}
export function InvoiceFlowPanel() {
  const prefersReducedMotion = useReducedMotion();
  const [paidCount, setPaidCount] = useState(0);
  useEffect(() => {
    const isComplete = paidCount >= invoices.length;
    const timer = window.setTimeout(() => setPaidCount(isComplete ? 0 : paidCount + 1), isComplete ? 2600 : paidCount === 0 ? 900 : 700);
    return () => window.clearTimeout(timer);
  }, [paidCount]);
  const outstanding = invoices.slice(paidCount).reduce((total, invoice) => total + invoice.amount, 0);
  return <div className="rounded-2xl border border-[#A6B6F8]/60 bg-white shadow-[0_20px_60px_rgba(4,31,96,0.12)]" role="img" aria-label="Credit Manager clearing overdue invoices as they are paid." data-id="element-94" data-mp="39fvow1p6hsgk">
      <div className="navy-texture flex items-center justify-between gap-4 rounded-t-2xl px-5 py-4" data-id="element-95" data-mp="30fpeioos18lm">
        <div data-id="element-96" data-mp="yr1vfa0ov4p">
          <p className="text-lg font-bold tracking-[-0.02em] text-white sm:text-xl" data-id="element-97" data-mp="yiqjrtjlxvm">
            Invoices outstanding
          </p>
        </div>
        <div className="text-right" data-id="element-98" data-mp="3ocwgm57ddnf1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#A6B6F8]" data-id="element-99" data-mp="3obur4vplwyds">
            Total overdue
          </p>
          <motion.p key={outstanding} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: -10,
          scale: 1.12
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease: easeOut
        }} className="text-3xl font-bold tracking-[-0.03em] tabular-nums" style={{
          color: outstanding > 0 ? '#FF8A8A' : '#4BE4FF'
        }} data-id="element-100" data-mp="3jdjk0sqp51z9">
            {currency.format(outstanding)}
          </motion.p>
        </div>
      </div>

      <ul className="divide-y divide-[#A6B6F8]/40 rounded-b-2xl" data-id="element-101" data-mp="3rkkyuspf638f">
        {invoices.map((invoice, index) => {
        const isPaid = index < paidCount;
        const justPaid = index === paidCount - 1;
        return <motion.li key={invoice.id} className="relative flex items-center gap-4 overflow-hidden px-5 py-4" animate={{
          scale: justPaid && !prefersReducedMotion ? [1, 1.035, 1] : 1
        }} transition={{
          duration: 0.3,
          ease: easeOut
        }} data-id="element-102" data-mp="n6mtsb6qgliv" data-mpi={index}>
              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] origin-top bg-[#4BE4FF]" animate={{
            scaleY: isPaid ? 1 : 0
          }} transition={{
            duration: 0.26,
            ease: easeOut
          }} data-id="element-103" data-mp="122kmfjhg77qc" />
              {justPaid && !prefersReducedMotion && <PaidCelebration key={`celebrate-${paidCount}`} data-id="element-104" data-mp="1q00di2nofwvw" />}

              <motion.div className="min-w-0 flex-1" animate={{
            opacity: isPaid ? 0.92 : 1
          }} transition={{
            duration: 0.24,
            ease: easeOut
          }} data-id="element-105" data-mp="1l0xf3127coww">
                <motion.p className="truncate text-sm font-bold" animate={{
              color: isPaid ? '#5B5299' : '#041F60'
            }} transition={{
              duration: 0.28,
              ease: easeOut
            }} data-id="element-106" data-mp="t9zp44r7zif4">
                  {invoice.customer}
                </motion.p>
                <p className="mt-0.5 truncate text-xs text-stone-500" data-id="element-107" data-mp="2inwgfp3ual14">
                  {invoice.reference} · {isPaid ? 'Paid in full' : invoice.age}
                </p>
              </motion.div>

              <div className="flex shrink-0 flex-col items-end gap-1.5" data-id="element-108" data-mp="2lqk7sm0xjz8c">
                <motion.p className="text-sm font-bold tabular-nums" animate={{
              color: isPaid ? '#5B5299' : '#041F60'
            }} transition={{
              duration: 0.28,
              ease: easeOut
            }} data-id="element-109" data-mp="2prm8766l2hbw">
                  {currency.format(invoice.amount)}
                </motion.p>
                <motion.span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]" animate={{
              backgroundColor: isPaid ? '#4BE4FF' : '#FEE2E2',
              color: isPaid ? '#041F60' : '#B91C1C',
              scale: justPaid && !prefersReducedMotion ? [1, 1.18, 1] : 1
            }} transition={{
              duration: 0.3,
              ease: easeOut
            }} data-id="element-110" data-mp="222ju28b5r6m5">
                  <motion.span key={isPaid ? 'paid' : 'overdue'} initial={prefersReducedMotion ? false : {
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.24,
                ease: easeOut
              }} className="block" data-id="element-111" data-mp="1oa3lyt3egied">
                    {isPaid ? 'Paid' : 'Overdue'}
                  </motion.span>
                </motion.span>
              </div>
            </motion.li>;
      })}
      </ul>
    </div>;
}